import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CommandDialog, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";
import { ShoppingCart, Users, Package } from "lucide-react";

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ orders: [], klanten: [], producten: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults({ orders: [], klanten: [], producten: [] });
      return;
    }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults({ orders: [], klanten: [], producten: [] });
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      api.get("/search", { params: { q: query } })
        .then(({ data }) => setResults(data))
        .finally(() => setLoading(false));
    }, 200);
  }, [query]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Zoek orders, klanten, producten…  (ordernr, e-mail, bedrijf, SKU)"
        data-testid="cmdk-input"
      />
      <CommandList>
        {!loading && query.length >= 2 &&
          results.orders.length === 0 &&
          results.klanten.length === 0 &&
          results.producten.length === 0 && (
            <CommandEmpty>Geen resultaten voor "{query}"</CommandEmpty>
          )}
        {loading && <div className="px-4 py-6 text-sm text-slate-400">Zoeken…</div>}

        {results.orders.length > 0 && (
          <CommandGroup heading="Orders">
            {results.orders.map((o) => (
              <CommandItem
                key={`o-${o.ordernummer}`}
                value={`order ${o.ordernummer} ${o.label}`}
                onSelect={() => go(`/orders?search=${encodeURIComponent(o.ordernummer)}`)}
                data-testid={`cmdk-order-${o.ordernummer}`}
              >
                <ShoppingCart className="h-4 w-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div className="font-medium">{o.ordernummer} · {o.label}</div>
                  <div className="text-xs text-slate-500">{o.datum}</div>
                </div>
                <span className="text-xs text-slate-600 font-medium">€{Number(o.totaal).toFixed(2)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.klanten.length > 0 && (
          <CommandGroup heading="Klanten">
            {results.klanten.map((k) => (
              <CommandItem
                key={`k-${k.email}`}
                value={`klant ${k.email} ${k.label}`}
                onSelect={() => go(`/klanten-analyse?search=${encodeURIComponent(k.email)}`)}
                data-testid={`cmdk-klant-${k.email}`}
              >
                <Users className="h-4 w-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div className="font-medium">{k.label || k.email}</div>
                  <div className="text-xs text-slate-500">{k.email}</div>
                </div>
                <span className="text-xs text-slate-600">{k.aantal_orders} orders</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.producten.length > 0 && (
          <CommandGroup heading="Producten">
            {results.producten.map((p) => (
              <CommandItem
                key={`p-${p.productnummer}`}
                value={`product ${p.productnummer} ${p.productnaam}`}
                onSelect={() => go(`/producten-analyse`)}
                data-testid={`cmdk-product-${p.productnummer}`}
              >
                <Package className="h-4 w-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div className="font-medium">{p.productnaam}</div>
                  <div className="text-xs text-slate-500">{p.productnummer}</div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
